import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { firestoreAction, vuexfireMutations } from 'vuexfire';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import MFAPhoneAuth from '../../plugins/mfa-phone-auth';

import analytics from '../../analytics';
import { logLoginError } from '../../utils/clientLogger';
import { version as appVersion } from '../../../package.json';

let apiHost = process.env.VUE_APP_API_HOST;

function b64DecodeUnicode(str) {
  // Going backwards: from bytestream, to percent-encoding, to original string.
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
}

async function extractToken(user) {
  // get the token to acces the custom claims
  let idToken = await user.getIdToken();
  // Parse the ID token.
  const payload = JSON.parse(b64DecodeUnicode(idToken.split('.')[1]));

  // get the id
  let accountId = payload['accountId'];
  let role = payload['role'];

  // Extract user info into a plain object (don't mutate the Firebase user object)
  const info = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    phoneNumber: user.phoneNumber,
    emailVerified: user.emailVerified,
    customerId: accountId || '',
    role: role,
  };

  return { info, token: idToken };
}

async function loadPermissions(token, role) {
  let response = await axios.get(`${apiHost}/auth/permissions?role=${role}`, {
    headers: {
      Authorization: token,
    },
  });
  return response.data;
}

const state = {
  authenticated: false,
  authenticating: true,
  info: {},
  permissions: [],
  customer: {},
  prefs: { emailAlerts: {}, favoriteProjects: {} },
  token: '',
  users: [],
  // MFA state
  mfaResolver: null,
  mfaVerificationId: null,
  mfaEnabled: false, // Track if user has MFA enabled (for native platform)
};

const mutations = {
  setAuthenticating(state, { authenticating }) {
    state.authenticating = authenticating;
  },
  authenticated(state, { success, info = {}, permissions = {}, token = '' }) {
    state.authenticated = success;
    let defaults = {
      displayName: '',
      email: '',
      phoneNumber: '',
      customerId: '',
      role: '',
      emailSettings: {
        onOrderReceived: false,
        onBoxSet: false,
        onPullComplete: false,
      },
    };
    let standardized = Object.assign(defaults, info);
    state.info = standardized;
    state.permissions = permissions;
    state.token = token;
    state.authenticating = false;
    analytics.logEvent('login', {
      customer_id: standardized.customerId,
      identifier: standardized.email,
    });
  },
  updateToken(state, { token }) {
    state.token = token;
  },
  updatePrefs: (state, { prefs }) => {
    state.prefs = {
      ...state.prefs,
      ...prefs,
      favoriteProjects: {
        ...prefs.favoriteProjects,
      },
    };
    let ref = firebase
      .firestore()
      .collection('users')
      .doc(state.info.uid);

    ref.update(state.prefs);
  },
  setCustomer(state, { customer }) {
    state.customer = customer;
  },
  setUsers(state, { users }) {
    state.users = users;
  },
  setMfaResolver(state, { resolver }) {
    state.mfaResolver = resolver;
  },
  setMfaVerificationId(state, { verificationId }) {
    state.mfaVerificationId = verificationId;
  },
  clearMfaState(state) {
    state.mfaResolver = null;
    state.mfaVerificationId = null;
  },
  setMfaEnabled(state, { enabled }) {
    state.mfaEnabled = enabled;
  },
  ...vuexfireMutations,
};

const actions = {
  bindRef: firestoreAction(({ bindFirestoreRef }, { name, ref }) => {
    bindFirestoreRef(name, ref);
  }),
  restoreSignIn: async ({ commit, dispatch }, { user }) => {
    commit('setAuthenticating', { authenticating: true });

    let { info, token } = await extractToken(user);
    dispatch('bindRef', {
      name: 'prefs',
      ref: firebase
        .firestore()
        .collection('users')
        .doc(info.uid),
    });

    let permissions = await loadPermissions(token, info.role);

    commit('authenticated', { success: true, info, permissions, token });

    // Fetch customer locations after successful authentication
    dispatch('locations/fetchCustomerFamily', null, { root: true });
  },
  // Restore sign-in from native MFA flow (native and JS Firebase SDKs don't share auth state)
  restoreSignInFromNative: async (
    { commit, dispatch },
    {
      idToken,
      uid,
      email,
      displayName,
      phoneNumber,
      emailVerified,
      mfaEnabled = false,
    }
  ) => {
    commit('setAuthenticating', { authenticating: true });

    // Parse the ID token to get custom claims (same as extractToken but without user object)
    const payload = JSON.parse(b64DecodeUnicode(idToken.split('.')[1]));
    const accountId = payload['accountId'];
    const role = payload['role'];

    const info = {
      uid,
      email,
      displayName,
      phoneNumber,
      // Use provided emailVerified or fall back to token payload
      emailVerified:
        emailVerified !== undefined
          ? emailVerified
          : payload['email_verified'] || false,
      customerId: accountId || '',
      role,
    };

    // Bind Firestore prefs
    dispatch('bindRef', {
      name: 'prefs',
      ref: firebase
        .firestore()
        .collection('users')
        .doc(uid),
    });

    let permissions = await loadPermissions(idToken, role);

    commit('authenticated', {
      success: true,
      info,
      permissions,
      token: idToken,
    });

    // Set MFA enabled status for native platform
    commit('setMfaEnabled', { enabled: mfaEnabled });

    // Fetch customer locations after successful authentication
    dispatch('locations/fetchCustomerFamily', null, { root: true });
  },
  authenticate: async ({ dispatch, commit }, { username, password }) => {
    commit('setAuthenticating', { authenticating: true });

    try {
      if (Capacitor.isNativePlatform()) {
        // Mobile: Use native plugin for MFA-aware sign-in
        console.log('📱 Using native MFA sign-in');
        const result = await MFAPhoneAuth.signInWithMfa({
          email: username,
          password,
        });

        if (result.requiresMfa) {
          // MFA required - verification code already sent natively
          console.log('📱 MFA required, code sent to:', result.phoneNumber);
          commit('setMfaVerificationId', {
            verificationId: result.verificationId,
          });
          commit('setMfaResolver', { resolver: 'native' }); // Mark as native flow
          commit('setAuthenticating', { authenticating: false });
          return {
            success: false,
            requiresMFA: true,
            codeSent: true, // Code already sent by native signInWithMfa
            hints: [{ phoneNumber: result.phoneNumber }],
          };
        } else {
          // Sign-in successful without MFA
          // Native and JS Firebase SDKs don't share auth state, use returned token
          console.log('📱 Non-MFA sign-in successful, using native token');
          commit('clearMfaState');
          await dispatch('restoreSignInFromNative', {
            idToken: result.idToken,
            uid: result.uid,
            email: result.email,
            displayName: result.displayName,
            phoneNumber: result.phoneNumber,
          });
          return { success: true };
        }
      }

      // Web: Use standard Firebase JS SDK
      let auth = firebase.auth();
      let { user } = await auth.signInWithEmailAndPassword(username, password);

      commit('clearMfaState');
      await dispatch('restoreSignIn', { user });
      return { success: true };
    } catch (err) {
      // Check if MFA is required (web flow)
      if (err.code === 'auth/multi-factor-auth-required') {
        console.log('user.js authenticate - requires MFA');
        // Store the resolver for MFA verification
        commit('setMfaResolver', { resolver: err.resolver });
        commit('setAuthenticating', { authenticating: false });
        return {
          success: false,
          requiresMFA: true,
          resolver: err.resolver,
          hints: err.resolver.hints,
        };
      }

      logLoginError({
        eventType: 'client_login_error',
        errorCode: err.code,
        errorMessage: err.message,
        email: username,
        platform: Capacitor.getPlatform(),
        appVersion,
      });
      commit('authenticated', { success: false });
      return { success: false, error: err };
    }
  },
  logOut: async ({ commit, dispatch }) => {
    try {
      // Sign out from JS Firebase SDK
      firebase.auth().signOut();

      // Also sign out from native SDK to clear persisted session
      if (Capacitor.isNativePlatform()) {
        try {
          await MFAPhoneAuth.signOutNatively();
          console.log('📱 Signed out from native Firebase');
        } catch (nativeErr) {
          console.error('📱 Error signing out natively:', nativeErr);
        }
      }
    } catch (err) {
      window.console.error(err);
    }

    // Reset MFA state
    commit('setMfaEnabled', { enabled: false });

    // Reset multiple locations flag
    if (window.app && window.app.$data) {
      window.app.$data.multipleLocations = false;
    }

    // Reset locations when logging out
    dispatch('locations/resetLocations', null, { root: true });

    commit('authenticated', { success: false, info: {} });
  },
  loadUsers: async ({ dispatch, state, commit }) => {
    await dispatch('refreshToken');
    try {
      let response = await axios.get(`${apiHost}/auth/list-customer-users`, {
        headers: {
          Authorization: state.token,
        },
      });
      let users = response.data;
      commit('setUsers', { users });
    } catch (err) {
      // let res = err.response;
      commit('setUsers', { users: [] });
    }
  },
  deleteUser: async ({ dispatch, state }, { uid }) => {
    await dispatch('refreshToken');
    try {
      let response = await axios.post(
        `${apiHost}/auth/delete-user`,
        {
          uid: uid,
        },
        {
          headers: {
            Authorization: state.token,
          },
        }
      );

      await dispatch('loadUsers');

      return response;
    } catch (error) {
      return { error };
    }
  },
  updateUserRole: async ({ dispatch, state }, { email, role }) => {
    await dispatch('refreshToken');
    try {
      let response = await axios.post(
        `${apiHost}/auth/update-user-role`,
        {
          email,
          role,
        },
        {
          headers: {
            Authorization: state.token,
          },
        }
      );

      await dispatch('loadUsers');

      return response;
    } catch (error) {
      return { error };
    }
  },
  refreshToken: async ({ commit, state }) => {
    // Web: use Firebase JS SDK
    const user = firebase.auth().currentUser;
    if (user) {
      const { token } = await extractToken(user);
      commit('updateToken', { token });
      return;
    }

    // Native: use native plugin to refresh token
    if (Capacitor.isNativePlatform() && state.authenticated) {
      try {
        const result = await MFAPhoneAuth.refreshToken();
        if (result.idToken) {
          commit('updateToken', { token: result.idToken });
        }
      } catch (err) {
        window.console.error('Native token refresh failed:', err);
      }
    }
  },
  refreshLocations: ({ dispatch, state }) => {
    if (state.authenticated) {
      dispatch('locations/fetchCustomerFamily', null, { root: true });
    }
  },

  // ========== MFA Actions ==========

  /**
   * Verify MFA code during sign-in
   * @param {string} code - The SMS verification code from the user
   */
  verifyMfaCode: async ({ state, commit, dispatch }, { code }) => {
    try {
      if (!state.mfaResolver) {
        throw new Error('No MFA resolver available');
      }

      const verificationId = state.mfaVerificationId;

      if (Capacitor.isNativePlatform() && state.mfaResolver === 'native') {
        // Mobile: Complete MFA sign-in natively
        console.log('📱 Completing MFA sign-in natively');
        const result = await MFAPhoneAuth.completeMfaSignIn({
          verificationId,
          verificationCode: code,
        });

        console.log('📱 Native MFA sign-in result:', result);

        // Native and JS Firebase SDKs don't share auth state
        // Use the returned token and user info directly
        commit('clearMfaState');
        return dispatch('restoreSignInFromNative', {
          idToken: result.idToken,
          uid: result.uid,
          email: result.email,
          displayName: result.displayName,
          phoneNumber: result.phoneNumber,
          mfaEnabled: true, // User completed MFA sign-in, so MFA is enabled
        });
      }

      // Web: Use JS SDK
      const cred = firebase.auth.PhoneAuthProvider.credential(
        verificationId,
        code
      );
      const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(
        cred
      );

      // Complete sign-in with MFA
      const userCredential = await state.mfaResolver.resolveSignIn(
        multiFactorAssertion
      );

      commit('clearMfaState');
      return dispatch('restoreSignIn', { user: userCredential.user });
    } catch (err) {
      logLoginError({
        eventType: 'client_mfa_verify_failed',
        errorCode: err.code,
        errorMessage: err.message,
        email: state.info && state.info.email,
        platform: Capacitor.getPlatform(),
        appVersion,
      });
      commit('setAuthenticating', { authenticating: false });
      throw err;
    }
  },

  /**
   * Send MFA verification code to user's phone
   * Called automatically after MFA is required during login (web only)
   * On mobile, the code is sent during signInWithMfa, use resendMfaCode for resend
   */
  sendMfaVerificationCode: async ({ state, commit }) => {
    try {
      if (!state.mfaResolver) {
        throw new Error('No MFA resolver available');
      }

      // On mobile with native flow, this shouldn't be called for initial send
      // (code is sent during signInWithMfa). For resend, use resendMfaCode.
      if (Capacitor.isNativePlatform() && state.mfaResolver === 'native') {
        throw new Error('Use resendMfaCode for mobile platform');
      }

      // Web: Use reCAPTCHA
      const selectedHint = state.mfaResolver.hints[0];
      console.log('user.js sendMfaVerificationCode (web)');

      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
      const phoneInfoOptions = {
        multiFactorHint: selectedHint,
        session: state.mfaResolver.session,
      };
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        window.recaptchaVerifier
      );

      commit('setMfaVerificationId', { verificationId });
      return { success: true, verificationId };
    } catch (err) {
      logLoginError({
        eventType: 'client_mfa_send_failed',
        errorCode: err.code,
        errorMessage: err.message,
        email: state.info && state.info.email,
        platform: Capacitor.getPlatform(),
        appVersion,
      });
      throw err;
    }
  },

  /**
   * Resend MFA verification code (mobile only)
   * Uses the stored native MFA resolver to send a new code
   */
  resendMfaCode: async ({ state, commit }) => {
    try {
      if (Capacitor.isNativePlatform() && state.mfaResolver === 'native') {
        console.log('📱 Resending MFA code natively');
        const result = await MFAPhoneAuth.resendMfaCode();
        commit('setMfaVerificationId', {
          verificationId: result.verificationId,
        });
        return { success: true, verificationId: result.verificationId };
      } else {
        // Web: Use the standard sendMfaVerificationCode
        throw new Error('Use sendMfaVerificationCode for web platform');
      }
    } catch (err) {
      logLoginError({
        eventType: 'client_mfa_resend_failed',
        errorCode: err.code,
        errorMessage: err.message,
        email: state.info && state.info.email,
        platform: Capacitor.getPlatform(),
        appVersion,
      });
      throw err;
    }
  },

  /**
   * Enroll user in MFA (from settings/profile page)
   * @param {string} phoneNumber - Phone number in E.164 format (e.g., +1234567890)
   * @param {string} password - User's password (required on mobile for native auth sync)
   */
  // eslint-disable-next-line no-unused-vars
  enrollMFA: async (_context, { phoneNumber, password }) => {
    try {
      window.console.log('🔐 Starting MFA enrollment for:', phoneNumber);

      let verificationId;

      if (Capacitor.isNativePlatform()) {
        // Mobile: reauthenticateWithPassword already called signInNatively,
        // so native auth state is synced. Just verify the phone.
        window.console.log('📱 Using native MFA phone auth plugin');
        const result = await MFAPhoneAuth.verifyPhoneForEnrollment({
          phoneNumber,
        });
        verificationId = result.verificationId;
      } else {
        // Web: Use JS Firebase SDK + reCAPTCHA
        const user = firebase.auth().currentUser;
        if (!user) {
          throw new Error('No authenticated user');
        }
        window.console.log('✓ User authenticated:', user.uid);
        window.console.log('🌐 Using reCAPTCHA verifier');
        window.console.log('Getting MFA session...');
        const session = await user.multiFactor.getSession();
        window.console.log('✓ Got MFA session');

        const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
        verificationId = await phoneAuthProvider.verifyPhoneNumber(
          { phoneNumber, session },
          window.recaptchaVerifier
        );
      }

      window.console.log('✓ Verification ID received');
      return { success: true, verificationId };
    } catch (err) {
      window.console.error('Error enrolling in MFA:', err);
      throw err;
    }
  },

  /**
   * Complete MFA enrollment with verification code
   * @param {string} verificationId - The verification ID from enrollMFA
   * @param {string} code - The SMS verification code
   * @param {string} displayName - Display name for the MFA factor
   */
  verifyMFAEnrollment: async (
    { commit },
    { verificationId, code, displayName = 'Phone' }
  ) => {
    try {
      if (Capacitor.isNativePlatform()) {
        // Native: Use native plugin to complete enrollment
        await MFAPhoneAuth.completeEnrollment({
          verificationId,
          verificationCode: code,
          displayName,
        });
        commit('setMfaEnabled', { enabled: true });
        return { success: true };
      } else {
        // Web: Use JS Firebase SDK
        const user = firebase.auth().currentUser;
        if (!user) {
          throw new Error('No authenticated user');
        }

        const cred = firebase.auth.PhoneAuthProvider.credential(
          verificationId,
          code
        );
        const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(
          cred
        );

        await user.multiFactor.enroll(multiFactorAssertion, displayName);
        return { success: true };
      }
    } catch (err) {
      window.console.error('Error verifying MFA enrollment:', err);
      throw err;
    }
  },

  /**
   * Unenroll from MFA
   * @param {string} factorUid - The UID of the MFA factor to remove
   */
  unenrollMFA: async (_context, { factorUid }) => {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Find the factor to unenroll
      const factor = user.multiFactor.enrolledFactors.find(
        f => f.uid === factorUid
      );
      if (!factor) {
        throw new Error('MFA factor not found');
      }

      await user.multiFactor.unenroll(factor);
      return { success: true };
    } catch (err) {
      window.console.error('Error unenrolling from MFA:', err);
      throw err;
    }
  },

  /**
   * Get list of enrolled MFA factors for current user
   */
  getMFAFactors: async ({ state }) => {
    try {
      const user = firebase.auth().currentUser;
      if (user) {
        return user.multiFactor.enrolledFactors || [];
      }

      // Native platform: check if user went through MFA sign-in flow
      // If mfaEnabled flag is set in state, return a placeholder factor
      if (Capacitor.isNativePlatform() && state.mfaEnabled) {
        return [{ factorId: 'phone' }]; // Placeholder to indicate MFA is enabled
      }

      return [];
    } catch (err) {
      window.console.error('Error getting MFA factors:', err);
      return [];
    }
  },

  /**
   * Re-authenticate user with password
   * Required before sensitive operations like MFA enrollment
   * @param {string} password - The user's password
   */
  reauthenticateWithPassword: async ({ state }, { password }) => {
    try {
      if (Capacitor.isNativePlatform()) {
        // Native: Use native plugin to re-authenticate
        // signInNatively effectively re-authenticates the user
        const email = state.info && state.info.email;
        if (!email) {
          throw new Error('No authenticated user');
        }
        await MFAPhoneAuth.signInNatively({ email, password });
        return { success: true };
      }

      // Web: Use JS Firebase SDK
      const user = firebase.auth().currentUser;
      if (!user || !user.email) {
        throw new Error('No authenticated user');
      }

      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email,
        password
      );

      await user.reauthenticateWithCredential(credential);
      return { success: true };
    } catch (err) {
      window.console.error('Error during re-authentication:', err);

      // Check if MFA is required for re-authentication
      if (err.code === 'auth/multi-factor-auth-required') {
        // Return the resolver so the caller can handle MFA
        return {
          success: false,
          requiresMFA: true,
          resolver: err.resolver,
        };
      }

      // Native plugin returns error message as string
      const errMsg = err.message || '';
      if (
        err.code === 'auth/wrong-password' ||
        errMsg.includes('password is invalid')
      ) {
        throw new Error('Incorrect password');
      } else if (
        err.code === 'auth/too-many-requests' ||
        errMsg.includes('Too many')
      ) {
        throw new Error('Too many failed attempts. Please try again later.');
      } else if (
        err.code === 'mfa-required' ||
        errMsg.includes('MFA required')
      ) {
        // Native signInNatively rejects with 'mfa-required' when user has MFA enabled.
        // Use signInWithMfa to initiate the full native MFA flow (sends SMS code)
        // and return MFA info so the caller can prompt for verification.
        if (Capacitor.isNativePlatform()) {
          try {
            const email = state.info && state.info.email;
            const mfaResult = await MFAPhoneAuth.signInWithMfa({
              email,
              password,
            });

            if (mfaResult.requiresMfa) {
              return {
                success: false,
                requiresMFA: true,
                native: true,
                verificationId: mfaResult.verificationId,
                phoneNumber: mfaResult.phoneNumber,
              };
            }
            // No MFA required after all (shouldn't happen, but handle gracefully)
            return { success: true };
          } catch (mfaErr) {
            window.console.error(
              'Error initiating native MFA re-auth:',
              mfaErr
            );
            throw new Error(
              'Failed to initiate MFA verification. Please try again.'
            );
          }
        }
        // Non-native with mfa-required code shouldn't normally reach here
        return { success: true };
      } else {
        throw new Error('Authentication failed. Please try again.');
      }
    }
  },

  /**
   * Complete native MFA re-authentication
   * Used when re-authenticating on native platform requires MFA (e.g., password change)
   * Calls completeMfaSignIn natively and updates the store token
   * @param {string} verificationId - The verification ID from native MFA flow
   * @param {string} code - The SMS verification code
   */
  completeNativeMfaReauth: async ({ commit }, { verificationId, code }) => {
    try {
      const result = await MFAPhoneAuth.completeMfaSignIn({
        verificationId,
        verificationCode: code,
      });
      // Update store with fresh token from completed MFA sign-in
      commit('updateToken', { token: result.idToken });
      return { success: true };
    } catch (err) {
      window.console.error('Native MFA re-auth error:', err);
      throw err;
    }
  },

  /**
   * Complete MFA re-authentication (web)
   * Used when re-authenticating a user who has MFA enabled
   * @param {object} resolver - The MFA resolver from the re-authentication error
   * @param {string} verificationId - The verification ID from SMS
   * @param {string} code - The SMS verification code
   */
  reauthenticateWithMFA: async (
    _context,
    { resolver, verificationId, code }
  ) => {
    try {
      const cred = firebase.auth.PhoneAuthProvider.credential(
        verificationId,
        code
      );
      const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(
        cred
      );

      // Complete re-authentication with MFA
      await resolver.resolveSignIn(multiFactorAssertion);
      return { success: true };
    } catch (err) {
      window.console.error('MFA re-authentication error:', err);
      throw err;
    }
  },

  /**
   * Send MFA verification code during re-authentication
   * @param {object} resolver - The MFA resolver from the re-authentication error
   */
  sendReauthMfaCode: async (_context, { resolver }) => {
    try {
      const selectedHint = resolver.hints[0];
      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
      const phoneInfoOptions = {
        multiFactorHint: selectedHint,
        session: resolver.session,
      };

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        window.recaptchaVerifier
      );

      return {
        success: true,
        verificationId,
        phoneNumber: selectedHint.phoneNumber,
      };
    } catch (err) {
      window.console.error('Error sending re-auth MFA code:', err);
      throw err;
    }
  },

  // ========== Email Verification Actions ==========

  /**
   * Send email verification to current user
   * Required before user can enroll in MFA
   * Uses server API to send custom email template
   */
  sendEmailVerification: async ({ state }) => {
    try {
      // Check if user is authenticated (works for both web and native)
      if (!state.authenticated || !state.token) {
        throw new Error('No authenticated user');
      }

      // Check email verification status (use fresh data from server)
      const user = firebase.auth().currentUser;
      if (user) {
        await user.reload();
        if (user.emailVerified) {
          return { success: true, alreadyVerified: true };
        }
      } else if (Capacitor.isNativePlatform()) {
        // Native: reload user to get fresh status
        try {
          const reloadResult = await MFAPhoneAuth.reloadUser();
          if (reloadResult.emailVerified) {
            return { success: true, alreadyVerified: true };
          }
        } catch (reloadErr) {
          // If reload fails, continue to send verification email
          window.console.warn('Could not check email status:', reloadErr);
        }
      }

      // Call server API to send custom verification email
      const response = await axios.post(
        `${apiHost}/auth/send-email-verification`,
        {},
        {
          headers: {
            Authorization: state.token,
          },
        }
      );

      return response.data;
    } catch (err) {
      window.console.error('Error sending email verification:', err);
      throw err;
    }
  },

  /**
   * Check if current user's email is verified
   * On both web and native, reloads the user profile to get fresh status
   */
  checkEmailVerified: async ({ state, commit }) => {
    try {
      const user = firebase.auth().currentUser;
      if (user) {
        // Web: Reload user to get latest email verification status
        await user.reload();
        return user.emailVerified;
      }

      // Native platform: reload user via native plugin to get fresh status
      if (Capacitor.isNativePlatform() && state.authenticated) {
        const result = await MFAPhoneAuth.reloadUser();
        // Update stored info with fresh emailVerified status
        if (state.info && result.emailVerified !== state.info.emailVerified) {
          commit('authenticated', {
            success: true,
            info: { ...state.info, emailVerified: result.emailVerified },
            permissions: state.permissions,
            token: state.token,
          });
        }
        return result.emailVerified || false;
      }

      return false;
    } catch (err) {
      window.console.error('Error checking email verification:', err);
      return false;
    }
  },
};

let user = {
  namespaced: true,
  state,
  mutations,
  actions,
};

export default user;
